(() => {
  const TRENDING_IMAGE_WIDTH = 480;

  const escapeTrendingHtml = (value) =>
    String(value ?? "").replace(
      /[&<>"']/g,
      (char) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[char]
    );

  const formatTrendingMoney = (amount, moneyFormat) => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (Number.isNaN(cents)) return "";
    if (typeof window.Shopify?.formatMoney === "function") {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    return `£${(cents / 100).toFixed(2)}`;
  };

  const trendingImageUrl = (url, width) => {
    if (!url) return "";
    try {
      const imageUrl = new URL(url, window.location.origin);
      imageUrl.searchParams.set("width", width);
      return imageUrl.toString();
    } catch (error) {
      return url;
    }
  };

  const buildTrendingCardMarkup = (variant, moneyFormat) => {
    const productTitle = variant.title || "";
    const variantTitle =
      variant.variantTitle && variant.variantTitle !== "Default Title"
        ? variant.variantTitle
        : "";
    const price = parseFloat(variant.price);
    const compareAtPrice = parseFloat(variant.compareAtPrice);
    const onSale =
      !Number.isNaN(compareAtPrice) && compareAtPrice > price;
    const discountPercentage = onSale
      ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
      : 0;
    const url = variant.url || "#";
    const ariaLabel = variantTitle
      ? `${productTitle}, ${variantTitle}`
      : productTitle;
    const image = variant.image;
    const labels = Array.isArray(variant.labels) ? variant.labels : [];

    return `
      <li class="main-collection-trending__card" data-main-trending-card>
        <a
          class="main-collection-trending__card-link"
          href="${escapeTrendingHtml(url)}"
          aria-label="${escapeTrendingHtml(ariaLabel)}"
        >
          <div class="main-collection-trending__media">
            <div class="main-collection-trending__status">
              ${
                onSale
                  ? `<span class="main-collection-trending__status-label main-collection-trending__status-label--sale">${discountPercentage}% off</span>`
                  : ""
              }
            </div>

            <div class="main-collection-trending__image">
              ${
                image?.url
                  ? `<img
                      src="${escapeTrendingHtml(trendingImageUrl(image.url, TRENDING_IMAGE_WIDTH))}"
                      alt="${escapeTrendingHtml(image.altText || productTitle)}"
                      loading="lazy"
                      width="480"
                    >`
                  : ""
              }

              ${
                labels.length
                  ? `<div class="main-collection-trending__labels">
                      ${labels
                        .filter((label) => label?.title)
                        .map(
                          (label) =>
                            `<span class="main-collection-trending__variant-label">${escapeTrendingHtml(label.title)}</span>`
                        )
                        .join("")}
                    </div>`
                  : ""
              }
            </div>
          </div>

          <div class="main-collection-trending__card-content">
            ${
              variant.vendor
                ? `<span class="main-collection-trending__vendor">${escapeTrendingHtml(variant.vendor)}</span>`
                : ""
            }

            <h3 class="main-collection-trending__product-title">${escapeTrendingHtml(productTitle)}</h3>

            ${
              variantTitle
                ? `<p class="main-collection-trending__variant-title">${escapeTrendingHtml(variantTitle)}</p>`
                : ""
            }

            <div
              class="main-collection-trending__reviews klaviyo-star-rating-widget"
              data-id="${escapeTrendingHtml(variant.productId)}"
              data-product-title="${escapeTrendingHtml(productTitle)}"
            ></div>

            <div class="main-collection-trending__price">
              <strong>${formatTrendingMoney(variant.price, moneyFormat)}</strong>
              ${onSale ? `<s>${formatTrendingMoney(variant.compareAtPrice, moneyFormat)}</s>` : ""}
            </div>
          </div>
        </a>

        <form
          class="main-collection-trending__quick-add"
          action="/cart/add"
          method="post"
          data-main-trending-add-form
        >
          <input type="hidden" name="id" value="${escapeTrendingHtml(variant.variantId)}">
          <input type="hidden" name="quantity" value="1">
          <button
            class="main-collection-trending__quick-add-button"
            type="submit"
            aria-label="Add ${escapeTrendingHtml(ariaLabel)} to cart"
            data-main-trending-add-button
          >
            <span class="main-collection-trending__quick-add-icon" aria-hidden="true"></span>
          </button>
          <span class="visually-hidden" aria-live="polite" data-main-trending-add-status></span>
        </form>
      </li>
    `;
  };

  const initMainCollection = (root) => {
    if (!root || root.dataset.mainCollectionReady === "true") return;
    root.dataset.mainCollectionReady = "true";

    const sectionScope =
      root.closest(".shopify-section") || root.parentElement || root;
    const shouldReturnToCollection = () =>
      window.matchMedia("(max-width: 859px)").matches && Boolean(root.id);
    const addCollectionAnchor = (url) => {
      if (shouldReturnToCollection()) url.hash = root.id;
      return url;
    };
    if (
      shouldReturnToCollection() &&
      window.location.hash === `#${root.id}`
    ) {
      const scrollToCollection = () =>
        root.scrollIntoView({ block: "start", behavior: "auto" });
      window.requestAnimationFrame(scrollToCollection);
      window.setTimeout(scrollToCollection, 350);
    }
    const sidebarPanel = root.querySelector(".main-collection-sidebar__panel");
    const removeDuplicatedFilterContainers = () => {
      if (!sidebarPanel) return;
      const filterContainers = Array.from(
        sidebarPanel.querySelectorAll(".main-collection-filters")
      );
      filterContainers.slice(1).forEach((container) => {
        if (container.parentNode) container.parentNode.removeChild(container);
      });
    };
    removeDuplicatedFilterContainers();
    window.requestAnimationFrame(removeDuplicatedFilterContainers);
    window.setTimeout(removeDuplicatedFilterContainers, 250);
    if (sidebarPanel && "MutationObserver" in window) {
      const filterContainerObserver = new MutationObserver(
        removeDuplicatedFilterContainers
      );
      filterContainerObserver.observe(sidebarPanel, {
        childList: true,
        subtree: true,
      });
    }
    const filterPanel = root.querySelector("[data-main-filter-panel]");
    const filterTrigger = root.querySelector("[data-main-filter-open]");
    const closeButtons = root.querySelectorAll("[data-main-filter-close]");

    const closeFilters = () => {
      if (!filterPanel) return;
      filterPanel.classList.remove("is-open");
      document.body.classList.remove("main-collection-filter-open");
      if (filterTrigger) {
        filterTrigger.setAttribute("aria-expanded", "false");
        filterTrigger.focus();
      }
    };

    const openFilters = () => {
      if (!filterPanel) return;
      filterPanel.classList.add("is-open");
      document.body.classList.add("main-collection-filter-open");
      if (filterTrigger) filterTrigger.setAttribute("aria-expanded", "true");
      const closeButton = filterPanel.querySelector(
        ".main-collection-sidebar__close"
      );
      if (closeButton) closeButton.focus();
    };

    if (filterTrigger) filterTrigger.addEventListener("click", openFilters);
    closeButtons.forEach((button) =>
      button.addEventListener("click", closeFilters)
    );

    root.querySelectorAll("[data-main-filter-toggle]").forEach((button) => {
      button.addEventListener("click", () => {
        const group = button.closest(".main-collection-filter-group");
        if (!group) return;

        const expanded = button.getAttribute("aria-expanded") === "true";
        group.querySelectorAll("[data-main-filter-hidden]").forEach((item) => {
          item.classList.toggle("is-hidden", expanded);
        });

        button.setAttribute("aria-expanded", String(!expanded));
        button.textContent = expanded
          ? button.dataset.moreText
          : button.dataset.lessText;
      });
    });

    root.querySelectorAll("[data-main-collection-sort]").forEach((select) => {
      select.addEventListener("change", () => {
        if (shouldReturnToCollection() && select.form) {
          const formAction = new URL(select.form.action, window.location.origin);
          formAction.hash = root.id;
          select.form.action = formAction.toString();
        }
        if (select.form.requestSubmit) {
          select.form.requestSubmit();
        } else {
          select.form.submit();
        }
      });
    });

    root.querySelectorAll("[data-main-filter-form]").forEach((form) => {
      form.addEventListener("change", (event) => {
        const control = event.target;
        if (!control.matches("input:not([type='hidden']), select")) return;
        if (control.closest("[data-main-nutrition-range]")) return;

        if (shouldReturnToCollection()) {
          const formAction = new URL(form.action, window.location.origin);
          formAction.hash = root.id;
          form.action = formAction.toString();
        }

        if (form.requestSubmit) {
          form.requestSubmit();
        } else {
          form.submit();
        }
      });
    });

    const parseNutritionValue = (rawValue, targetUnit) => {
      const normalizedValue = String(rawValue || "")
        .trim()
        .toLowerCase()
        .replace(/,/g, ".");
      const numberMatch = normalizedValue.match(/-?\d+(?:\.\d+)?/);
      if (!numberMatch) return Number.NaN;

      let numericValue = Number(numberMatch[0]);
      if (!Number.isFinite(numericValue)) return Number.NaN;

      const valueUsesMilligrams = /mg\b/.test(normalizedValue);
      const valueUsesGrams = !valueUsesMilligrams && /g\b/.test(normalizedValue);
      if (targetUnit === "mg" && valueUsesGrams) numericValue *= 1000;
      if (targetUnit === "g" && valueUsesMilligrams) numericValue /= 1000;
      return numericValue;
    };

    const formatNutritionValue = (value) => {
      if (!Number.isFinite(value)) return "0";
      return Number.isInteger(value)
        ? String(value)
        : String(Math.round(value * 10) / 10);
    };

    const nutritionDefinitions = {
      carbs: {
        unit: "g",
        minimum: 0,
        maximum: 300,
        step: 10,
        minimumParam: "min_carbs",
        maximumParam: "max_carbs",
        nativeParam: "filter.v.m.nutrition.carbs",
      },
      sodium: {
        unit: "mg",
        minimum: 0,
        maximum: null,
        step: 100,
        minimumParam: "min_sodium",
        maximumParam: "max_sodium",
        nativeParam: "filter.v.m.nutrition.sodium",
      },
    };
    const nutritionUrl = new URL(window.location.href);
    let shouldNormalizeNutritionUrl = false;

    const snapNutritionValue = (value, definition, direction = "nearest") => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return definition.minimum;
      const stepCount = (numericValue - definition.minimum) / definition.step;
      const snappedSteps = direction === "down"
        ? Math.floor(stepCount)
        : direction === "up"
          ? Math.ceil(stepCount)
          : Math.round(stepCount);
      return definition.minimum + snappedSteps * definition.step;
    };

    Object.entries(nutritionDefinitions).forEach(([kind, definition]) => {
      const legacyNativeValues = nutritionUrl.searchParams
        .getAll(definition.nativeParam)
        .flatMap((value) => value.split(","))
        .map((value) => parseNutritionValue(value, definition.unit))
        .filter(Number.isFinite);

      if (nutritionUrl.searchParams.has(definition.nativeParam)) {
        nutritionUrl.searchParams.delete(definition.nativeParam);
        shouldNormalizeNutritionUrl = true;

        if (legacyNativeValues.length) {
          const legacyMinimum = snapNutritionValue(
            Math.min(...legacyNativeValues),
            definition,
            "down"
          );
          let legacyMaximum = snapNutritionValue(
            Math.max(...legacyNativeValues),
            definition,
            "up"
          );
          if (Number.isFinite(definition.maximum)) {
            legacyMaximum = Math.min(legacyMaximum, definition.maximum);
          }

          nutritionUrl.searchParams.set(
            definition.minimumParam,
            formatNutritionValue(Math.max(definition.minimum, legacyMinimum))
          );
          nutritionUrl.searchParams.set(
            definition.maximumParam,
            formatNutritionValue(Math.max(definition.minimum, legacyMaximum))
          );
        }
      }

      const minimumAliases = kind === "carbs"
        ? ["minimum_carbs", "min_carbohydrates"]
        : ["minimum_sodium"];
      const maximumAliases = kind === "carbs"
        ? ["maximum_carbs", "max_carbohydrates"]
        : ["maximum_sodium"];

      [...minimumAliases, ...maximumAliases].forEach((alias) => {
        if (!nutritionUrl.searchParams.has(alias)) return;
        const targetParam = minimumAliases.includes(alias)
          ? definition.minimumParam
          : definition.maximumParam;
        nutritionUrl.searchParams.set(targetParam, nutritionUrl.searchParams.get(alias));
        nutritionUrl.searchParams.delete(alias);
        shouldNormalizeNutritionUrl = true;
      });

      Array.from(nutritionUrl.searchParams.keys()).forEach((parameterName) => {
        const compactMatch = parameterName.toLowerCase().match(
          /^(\d+(?:\.\d+)?)(mg|g)-(carbs?|carbohydrates?|sodium)$/
        );
        if (!compactMatch) return;
        const isMatchingKind = kind === "carbs"
          ? /^(carbs?|carbohydrates?)$/.test(compactMatch[3])
          : compactMatch[3] === "sodium";
        if (!isMatchingKind) return;

        const shortcutMinimum = parseNutritionValue(
          `${compactMatch[1]}${compactMatch[2]}`,
          definition.unit
        );
        nutritionUrl.searchParams.set(
          definition.minimumParam,
          formatNutritionValue(snapNutritionValue(shortcutMinimum, definition))
        );
        nutritionUrl.searchParams.delete(parameterName);
        shouldNormalizeNutritionUrl = true;
      });

      [definition.minimumParam, definition.maximumParam].forEach((parameterName) => {
        if (!nutritionUrl.searchParams.has(parameterName)) return;
        const parsedValue = parseNutritionValue(
          nutritionUrl.searchParams.get(parameterName),
          definition.unit
        );
        if (!Number.isFinite(parsedValue)) {
          nutritionUrl.searchParams.delete(parameterName);
          shouldNormalizeNutritionUrl = true;
          return;
        }

        let cleanValue = snapNutritionValue(parsedValue, definition);
        cleanValue = Math.max(definition.minimum, cleanValue);
        if (Number.isFinite(definition.maximum)) {
          cleanValue = Math.min(definition.maximum, cleanValue);
        }
        const formattedValue = formatNutritionValue(cleanValue);
        if (nutritionUrl.searchParams.get(parameterName) !== formattedValue) {
          nutritionUrl.searchParams.set(parameterName, formattedValue);
          shouldNormalizeNutritionUrl = true;
        }
      });
    });

    if (shouldNormalizeNutritionUrl) {
      nutritionUrl.searchParams.delete("page");
      window.location.replace(addCollectionAnchor(nutritionUrl).toString());
      return;
    }

    const getActiveNutritionBounds = () => Object.fromEntries(
      Object.entries(nutritionDefinitions).flatMap(([kind, definition]) => {
        const hasMinimum = nutritionUrl.searchParams.has(definition.minimumParam);
        const hasMaximum = nutritionUrl.searchParams.has(definition.maximumParam);
        if (!hasMinimum && !hasMaximum) return [];

        let minimum = hasMinimum
          ? parseNutritionValue(
              nutritionUrl.searchParams.get(definition.minimumParam),
              definition.unit
            )
          : definition.minimum;
        let maximum = hasMaximum
          ? parseNutritionValue(
              nutritionUrl.searchParams.get(definition.maximumParam),
              definition.unit
            )
          : Infinity;
        if (!Number.isFinite(minimum)) minimum = definition.minimum;
        if (!Number.isFinite(maximum)) maximum = Infinity;
        return [[kind, { minimum, maximum, unit: definition.unit }]];
      })
    );
    const activeNutritionBounds = getActiveNutritionBounds();
    const hasActiveNutritionBounds = Object.keys(activeNutritionBounds).length > 0;

    const getCardNutritionVariants = (card) => {
      try {
        const variants = JSON.parse(card.dataset.mainNutritionVariants || "[]");
        return Array.isArray(variants) ? variants : [];
      } catch (error) {
        return [];
      }
    };

    const cardMatchesNutritionBounds = (card) => {
      if (!hasActiveNutritionBounds) return true;
      return getCardNutritionVariants(card).some((variant) => {
        if (variant.available === false) return false;
        return Object.entries(activeNutritionBounds).every(([kind, bounds]) => {
          const value = parseNutritionValue(variant[kind], bounds.unit);
          return Number.isFinite(value) &&
            value >= bounds.minimum && value <= bounds.maximum;
        });
      });
    };

    const customNutritionParams = Object.values(nutritionDefinitions).flatMap(
      (definition) => [definition.minimumParam, definition.maximumParam]
    );
    root
      .querySelectorAll("[data-main-filter-form], .main-collection__sort")
      .forEach((form) => {
        customNutritionParams.forEach((parameterName) => {
          if (!nutritionUrl.searchParams.has(parameterName)) return;
          if (form.elements.namedItem(parameterName)) return;
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = parameterName;
          input.value = nutritionUrl.searchParams.get(parameterName);
          form.appendChild(input);
        });
      });

    const nutritionRanges = Array.from(
      root.querySelectorAll("[data-main-nutrition-range]")
    );
    const nutritionRangeSelections = new Map();

    const applyAllNutritionRanges = () => {
      const nextUrl = new URL(window.location.href);

      nutritionRangeSelections.forEach((selection) => {
        const selectedMinimum = selection.getMinimum();
        const selectedMaximum = selection.getMaximum();
        const { definition, filterParam, absoluteMinimum, absoluteMaximum } = selection;

        nextUrl.searchParams.delete(filterParam);
        nextUrl.searchParams.delete(definition.minimumParam);
        nextUrl.searchParams.delete(definition.maximumParam);

        if (selectedMinimum > absoluteMinimum) {
          nextUrl.searchParams.set(
            definition.minimumParam,
            formatNutritionValue(selectedMinimum)
          );
        }
        if (selectedMaximum < absoluteMaximum) {
          nextUrl.searchParams.set(
            definition.maximumParam,
            formatNutritionValue(selectedMaximum)
          );
        }
      });

      nextUrl.searchParams.delete("page");
      window.location.assign(addCollectionAnchor(nextUrl).toString());
    };

    nutritionRanges.forEach((range) => {
      const kind = range.dataset.mainNutritionKind;
      const unit = range.dataset.mainNutritionUnit;
      const filterParam = range.dataset.mainFilterParam;
      const group = range.closest(".main-collection-filter-group");
      const optionsByValue = new Map();

      range.querySelectorAll("[data-main-nutrition-option]").forEach((option) => {
        const numericValue = parseNutritionValue(
          option.dataset.label || option.dataset.value,
          unit
        );
        if (!Number.isFinite(numericValue)) return;

        optionsByValue.set(option.dataset.value, {
          rawValue: option.dataset.value,
          numericValue,
          active: option.dataset.active === "true",
          count: Number(option.dataset.count) || 0,
        });
      });

      const options = Array.from(optionsByValue.values()).sort(
        (first, second) => first.numericValue - second.numericValue
      );
      const distinctValues = [...new Set(options.map((option) => option.numericValue))];

      if (!filterParam || distinctValues.length < 2) {
        if (group) group.hidden = true;
        return;
      }

      const definition = nutritionDefinitions[kind];
      if (!definition) return;
      const absoluteMinimum = definition.minimum;
      const absoluteMaximum = Number.isFinite(definition.maximum)
        ? definition.maximum
        : Math.max(
            definition.step,
            snapNutritionValue(distinctValues[distinctValues.length - 1], definition, "up")
          );
      const inputStep = definition.step;
      const currentBounds = activeNutritionBounds[kind];
      let selectedMinimum = currentBounds?.minimum ?? absoluteMinimum;
      let selectedMaximum = Number.isFinite(currentBounds?.maximum)
        ? currentBounds.maximum
        : absoluteMaximum;
      selectedMinimum = Math.max(absoluteMinimum, Math.min(selectedMinimum, absoluteMaximum));
      selectedMaximum = Math.max(selectedMinimum, Math.min(selectedMaximum, absoluteMaximum));

      // The final slider step represents the displayed value and anything
      // above it. This keeps products beyond the UI cap (for example 1000mg+
      // sodium or 300g+ carbohydrate) in the results.
      if (currentBounds && selectedMaximum >= absoluteMaximum) {
        currentBounds.maximum = Infinity;
      }

      const minimumRange = range.querySelector("[data-main-nutrition-min-range]");
      const maximumRange = range.querySelector("[data-main-nutrition-max-range]");
      const minimumNumber = range.querySelector("[data-main-nutrition-min-number]");
      const maximumNumber = range.querySelector("[data-main-nutrition-max-number]");
      const rangeFill = range.querySelector("[data-main-nutrition-range-fill]");
      const scaleMinimum = range.querySelector("[data-main-nutrition-scale-min]");
      const scaleMaximum = range.querySelector("[data-main-nutrition-scale-max]");
      const resetButton = range.querySelector("[data-main-nutrition-reset]");
      const applyButton = range.querySelector("[data-main-nutrition-apply]");

      nutritionRangeSelections.set(kind, {
        definition,
        filterParam,
        absoluteMinimum,
        absoluteMaximum,
        getMinimum: () => selectedMinimum,
        getMaximum: () => selectedMaximum,
      });

      [minimumRange, maximumRange, minimumNumber, maximumNumber].forEach((input) => {
        if (!input) return;
        input.min = String(absoluteMinimum);
        input.max = String(absoluteMaximum);
        input.step = String(inputStep);
      });
      if (scaleMinimum) scaleMinimum.textContent = `${formatNutritionValue(absoluteMinimum)}${unit}`;
      if (scaleMaximum) scaleMaximum.textContent = `${formatNutritionValue(absoluteMaximum)}${unit}+`;

      const maximumLabel = () => selectedMaximum >= absoluteMaximum
        ? `${formatNutritionValue(absoluteMaximum)}${unit}+`
        : `${formatNutritionValue(selectedMaximum)}${unit}`;

      const updateActiveLabel = () => {
        sectionScope
          .querySelectorAll(`[data-main-nutrition-active-label="${kind}"]`)
          .forEach((label) => {
            label.textContent = `${kind === "carbs" ? "Carbohydrates" : "Sodium"}: ${formatNutritionValue(selectedMinimum)}${unit}–${maximumLabel()}`;
          });
      };

      const updateRange = (nextMinimum, nextMaximum) => {
        selectedMinimum = Math.max(
          absoluteMinimum,
          Math.min(snapNutritionValue(nextMinimum, definition), absoluteMaximum)
        );
        selectedMaximum = Math.max(
          selectedMinimum,
          Math.min(snapNutritionValue(nextMaximum, definition), absoluteMaximum)
        );

        if (minimumRange) minimumRange.value = String(selectedMinimum);
        if (maximumRange) maximumRange.value = String(selectedMaximum);
        if (minimumNumber) minimumNumber.value = formatNutritionValue(selectedMinimum);
        if (maximumNumber) maximumNumber.value = formatNutritionValue(selectedMaximum);

        const rangeSpan = absoluteMaximum - absoluteMinimum || 1;
        const startPercentage = ((selectedMinimum - absoluteMinimum) / rangeSpan) * 100;
        const endPercentage = ((selectedMaximum - absoluteMinimum) / rangeSpan) * 100;
        if (rangeFill) {
          rangeFill.style.left = `${startPercentage}%`;
          rangeFill.style.right = `${100 - endPercentage}%`;
        }

        if (minimumRange) minimumRange.setAttribute("aria-valuetext", `${formatNutritionValue(selectedMinimum)} ${unit}`);
        if (maximumRange) {
          maximumRange.setAttribute(
            "aria-valuetext",
            selectedMaximum >= absoluteMaximum
              ? `${formatNutritionValue(absoluteMaximum)} ${unit} or more`
              : `${formatNutritionValue(selectedMaximum)} ${unit}`
          );
        }
        updateActiveLabel();
      };

      minimumRange?.addEventListener("input", () => {
        updateRange(
          Math.min(Number(minimumRange.value), selectedMaximum),
          selectedMaximum
        );
      });
      maximumRange?.addEventListener("input", () => {
        updateRange(
          selectedMinimum,
          Math.max(Number(maximumRange.value), selectedMinimum)
        );
      });
      minimumNumber?.addEventListener("change", () => {
        updateRange(
          Math.min(Number(minimumNumber.value), selectedMaximum),
          selectedMaximum
        );
      });
      maximumNumber?.addEventListener("change", () => {
        updateRange(
          selectedMinimum,
          Math.max(Number(maximumNumber.value), selectedMinimum)
        );
      });
      resetButton?.addEventListener("click", () => {
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.delete(definition.minimumParam);
        nextUrl.searchParams.delete(definition.maximumParam);
        nextUrl.searchParams.delete(filterParam);
        nextUrl.searchParams.delete("page");
        window.location.assign(addCollectionAnchor(nextUrl).toString());
      });
      applyButton?.addEventListener("click", applyAllNutritionRanges);

      updateRange(selectedMinimum, selectedMaximum);
    });

    root.addEventListener("click", (event) => {
      const clearNutritionFilter = event.target.closest(
        "[data-main-nutrition-filter-clear]"
      );
      if (!clearNutritionFilter || !root.contains(clearNutritionFilter)) return;

      event.preventDefault();
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.delete(clearNutritionFilter.dataset.mainFilterParam);
      const kind = clearNutritionFilter.dataset.mainNutritionKind;
      const definition = nutritionDefinitions[kind];
      if (definition) {
        nextUrl.searchParams.delete(definition.minimumParam);
        nextUrl.searchParams.delete(definition.maximumParam);
      }
      nextUrl.searchParams.delete("page");
      window.location.assign(addCollectionAnchor(nextUrl).toString());
    });

    sectionScope.querySelectorAll("[data-main-trending-carousel]").forEach(
      (carousel) => {
        const track = carousel.querySelector("[data-main-trending-track]");
        const previousButton = carousel.querySelector(
          "[data-main-trending-previous]"
        );
        const nextButton = carousel.querySelector(
          "[data-main-trending-next]"
        );
        const controls = carousel.querySelector(
          ".main-collection-trending__controls"
        );

        if (!track) return;

        const updateCarouselControls = () => {
          const maximumScroll = Math.max(
            0,
            track.scrollWidth - track.clientWidth
          );

          if (controls) controls.hidden = maximumScroll <= 2;
          if (previousButton) previousButton.disabled = track.scrollLeft <= 2;
          if (nextButton) {
            nextButton.disabled = track.scrollLeft >= maximumScroll - 2;
          }
        };

        const scrollCarousel = (direction) => {
          const firstCard = track.querySelector("[data-main-trending-card]");
          const trackStyles = window.getComputedStyle(track);
          const gap = parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
          const cardWidth = firstCard?.getBoundingClientRect().width || 0;
          const scrollAmount = Math.max(
            cardWidth + gap,
            track.clientWidth * 0.8
          );
          const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
          ).matches;

          track.scrollBy({
            left: direction * scrollAmount,
            behavior: reduceMotion ? "auto" : "smooth",
          });
        };

        if (previousButton) {
          previousButton.addEventListener("click", () => scrollCarousel(-1));
        }
        if (nextButton) {
          nextButton.addEventListener("click", () => scrollCarousel(1));
        }

        let scrollFrame = null;
        track.addEventListener(
          "scroll",
          () => {
            if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
            scrollFrame = window.requestAnimationFrame(
              updateCarouselControls
            );
          },
          { passive: true }
        );

        if ("ResizeObserver" in window) {
          const resizeObserver = new ResizeObserver(updateCarouselControls);
          resizeObserver.observe(track);
        }

        window.requestAnimationFrame(updateCarouselControls);

        const collectionHandle = carousel.dataset.collectionHandle;
        const moneyFormat = carousel.dataset.moneyFormat;
        if (collectionHandle) {
          fetch(
            `/apps/top-sellers?collection=${encodeURIComponent(collectionHandle)}`,
            { headers: { Accept: "application/json" } }
          )
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
              const variants = (data?.variants || []).filter(
                (variant) =>
                  parseFloat(variant.price) > 0 &&
                  variant.productType !== "Upsell" &&
                  variant.status !== "UNLISTED"
              );

              if (!variants.length) {
                carousel.hidden = true;
                return;
              }

              track.innerHTML = variants
                .map((variant) => buildTrendingCardMarkup(variant, moneyFormat))
                .join("");
              carousel.removeAttribute("aria-busy");
              window.requestAnimationFrame(updateCarouselControls);
            })
            .catch(() => {
              carousel.hidden = true;
            });
        } else {
          carousel.hidden = true;
        }
      }
    );

    sectionScope.addEventListener("submit", async (event) => {
        const form = event.target.closest("[data-main-trending-add-form]");
        if (!form || !sectionScope.contains(form)) return;

        event.preventDefault();

        const button = form.querySelector("[data-main-trending-add-button]");
        const status = form.querySelector("[data-main-trending-add-status]");
        const variantInput = form.querySelector('input[name="id"]');
        const variantId = variantInput?.value;

        if (!button || !variantId || button.disabled) return;

        button.disabled = true;
        button.classList.remove("is-added", "is-error");
        button.classList.add("is-loading");
        button.setAttribute("aria-busy", "true");
        if (status) status.textContent = "Adding to cart";

        try {
          const response = await fetch("/cart/add.js", {
            method: "POST",
            credentials: "same-origin",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.description ||
                errorData.message ||
                "Unable to add this product"
            );
          }

          button.classList.remove("is-loading");
          button.classList.add("is-added");
          if (status) status.textContent = "Added to cart";
          document.dispatchEvent(new CustomEvent("cart:drawer:open"));

          window.setTimeout(() => {
            button.classList.remove("is-added");
          }, 1400);
        } catch (error) {
          button.classList.remove("is-loading");
          button.classList.add("is-error");
          if (status) {
            status.textContent =
              error.message || "Unable to add this product to cart";
          }

          window.setTimeout(() => {
            button.classList.remove("is-error");
          }, 2000);
        } finally {
          button.disabled = false;
          button.removeAttribute("aria-busy");
        }
    });

    root.addEventListener("click", (event) => {
      const link = event.target.closest(
        "[data-main-tag-filter-link], [data-main-facet-filter-link]"
      );

      if (
        !link ||
        !root.contains(link) ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const currentUrl = new URL(window.location.href);
      const nextUrl = new URL(link.href, window.location.origin);
      const preservedParameterNames = new Set([
        "sort_by",
        "view",
        "preview_theme_id",
        ...customNutritionParams,
      ]);
      const filterParam = link.dataset.mainFilterParam;
      const filterValue = link.dataset.mainFilterValue;
      const filterAction = link.dataset.mainFilterAction;

      currentUrl.searchParams.forEach((value, name) => {
        const shouldPreserve =
          name.startsWith("filter.") || preservedParameterNames.has(name);
        const isRemovedValue =
          filterAction === "remove" &&
          name === filterParam &&
          value === filterValue;

        if (!shouldPreserve || isRemovedValue) return;

        const nextValues = nextUrl.searchParams.getAll(name);
        if (!nextValues.includes(value)) {
          nextUrl.searchParams.append(name, value);
        }
      });

      if (filterAction === "remove" && filterParam) {
        const remainingValues = nextUrl.searchParams
          .getAll(filterParam)
          .filter((value) => value !== filterValue);

        nextUrl.searchParams.delete(filterParam);
        remainingValues.forEach((value) => {
          nextUrl.searchParams.append(filterParam, value);
        });
      } else if (filterAction === "add" && filterParam) {
        const nextValues = nextUrl.searchParams.getAll(filterParam);
        if (!nextValues.includes(filterValue)) {
          nextUrl.searchParams.append(filterParam, filterValue);
        }
      }

      event.preventDefault();
      window.location.assign(addCollectionAnchor(nextUrl).toString());
    });

    const tagCountElements = Array.from(
      root.querySelectorAll("[data-main-tag-count]")
    );
    const productsJsonUrl = root.dataset.mainProductsJsonUrl;
    const productTotal = Number(root.dataset.mainProductTotal) || 0;
    const hasActiveFilters =
      root.dataset.mainHasActiveFilters === "true";
    const filteredCurrentPage =
      Number(root.dataset.mainFilteredCurrentPage) || 1;
    const filteredPageCount =
      Number(root.dataset.mainFilteredPageCount) || 1;

    const normalizeTag = (tag) => String(tag || "").trim().toLowerCase();

    const getRenderedProducts = (scope) => {
      const productsById = new Map();

      scope.querySelectorAll("[data-main-product-id]").forEach((card) => {
        const productId = card.dataset.mainProductId;
        if (!productId || productsById.has(productId)) return;

        let productTags = [];
        try {
          productTags = JSON.parse(card.dataset.mainProductTags || "[]");
        } catch (_error) {
          productTags = [];
        }

        productsById.set(productId, {
          id: productId,
          tags: Array.isArray(productTags) ? productTags : [],
        });
      });

      return productsById;
    };

    const loadCollectionProducts = async () => {
      const products = [];
      const pageSize = 250;
      const expectedPages = Math.max(1, Math.ceil(productTotal / pageSize));

      for (let page = 1; page <= expectedPages; page += 1) {
        const pageUrl = new URL(productsJsonUrl, window.location.origin);
        pageUrl.searchParams.set("limit", String(pageSize));
        pageUrl.searchParams.set("page", String(page));

        const response = await fetch(pageUrl.toString(), {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          throw new Error("Unable to load collection products");
        }

        const data = await response.json();
        const pageProducts = Array.isArray(data.products) ? data.products : [];
        products.push(...pageProducts);

        if (pageProducts.length < pageSize) break;
      }

      return products;
    };

    const loadFilteredCollectionProducts = async () => {
      const productsById = getRenderedProducts(root);

      for (let page = 1; page <= filteredPageCount; page += 1) {
        if (page === filteredCurrentPage) continue;

        const pageUrl = new URL(window.location.href);
        pageUrl.searchParams.set("page", String(page));

        const response = await fetch(pageUrl.toString(), {
          credentials: "same-origin",
          headers: { Accept: "text/html" },
        });

        if (!response.ok) {
          throw new Error("Unable to load filtered collection products");
        }

        const pageHtml = await response.text();
        const pageDocument = new DOMParser().parseFromString(
          pageHtml,
          "text/html"
        );

        getRenderedProducts(pageDocument).forEach((product, productId) => {
          if (!productsById.has(productId)) {
            productsById.set(productId, product);
          }
        });
      }

      return Array.from(productsById.values());
    };

    const updateProductTagCounts = (products) => {
      const productCountsByTag = new Map();

      products.forEach((product) => {
        const productTags = Array.isArray(product.tags)
          ? product.tags
          : String(product.tags || "").split(",");

        new Set(productTags.map(normalizeTag).filter(Boolean)).forEach(
          (tagKey) => {
            productCountsByTag.set(
              tagKey,
              (productCountsByTag.get(tagKey) || 0) + 1
            );
          }
        );
      });

      tagCountElements.forEach((countElement) => {
        const tagKey = normalizeTag(countElement.dataset.mainTag);
        const productCount = productCountsByTag.get(tagKey) || 0;
        const item = countElement.closest(
          ".main-collection-filter-list__item"
        );

        countElement.textContent = `(${productCount})`;
        if (item) item.classList.toggle("is-disabled", productCount === 0);
      });

      root.querySelectorAll("[data-main-tag-filter-group]").forEach((group) => {
        const tagLimit = Number(group.dataset.mainTagLimit) || 10;
        const items = Array.from(
          group.querySelectorAll("[data-main-tag-filter-item]")
        );
        const relevantItems = items.filter((item) => {
          const countElement = item.querySelector("[data-main-tag-count]");
          const productCount = Number(
            (countElement?.textContent || "").replace(/\D/g, "")
          );
          const isActive = item.dataset.mainTagActive === "true";
          const isRelevant = productCount > 0 || isActive;

          item.hidden = !isRelevant;
          return isRelevant;
        });

        relevantItems.forEach((item, index) => {
          const isActive = item.dataset.mainTagActive === "true";
          const isOverflow = !isActive && index >= tagLimit;

          item.classList.toggle("is-hidden", isOverflow);
          if (isOverflow) {
            item.setAttribute("data-main-filter-hidden", "");
          } else {
            item.removeAttribute("data-main-filter-hidden");
          }
        });

        const moreItem = group.querySelector("[data-main-filter-more]");
        const moreButton = moreItem?.querySelector(
          "[data-main-filter-toggle]"
        );
        const hasOverflow = relevantItems.some((item) =>
          item.hasAttribute("data-main-filter-hidden")
        );

        if (moreItem) moreItem.hidden = !hasOverflow;
        if (moreButton) {
          moreButton.setAttribute("aria-expanded", "false");
          moreButton.textContent = moreButton.dataset.moreText;
        }

        group.hidden = relevantItems.length === 0;
      });
    };

    if (tagCountElements.length > 0 && productsJsonUrl) {
      const productLoader = hasActiveFilters
        ? loadFilteredCollectionProducts()
        : loadCollectionProducts();

      productLoader
        .then(updateProductTagCounts)
        .catch(() => {
          tagCountElements.forEach((countElement) => {
            if (!countElement.textContent.trim()) countElement.remove();
          });
        });
    }

    const productGrid = root.querySelector(".main-collection__variant-grid");
    const productCount = root.querySelector("[data-main-product-count]");
    if (productGrid && hasActiveNutritionBounds) {
      productGrid
        .querySelectorAll(".main-collection-variant-card")
        .forEach((card) => {
          if (!cardMatchesNutritionBounds(card)) card.remove();
        });
    }
    const updateProductCount = () => {
      const count = productGrid
        ? productGrid.querySelectorAll(".main-collection-variant-card").length
        : 0;

      if (productCount) {
        productCount.textContent = `${count} ${
          count === 1 ? "product" : "products"
        } shown`;
      }

      const noProducts = root.querySelector("[data-main-no-products]");
      if (noProducts) {
        noProducts.hidden =
          count !== 0 || root.classList.contains("is-nutrition-filtering");
      }

      return count;
    };

    updateProductCount();

    const infiniteScroll = root.querySelector("[data-main-infinite-scroll]");
    if (infiniteScroll && productGrid) {
      const status = infiniteScroll.querySelector(
        "[data-main-infinite-status]"
      );
      const retryButton = infiniteScroll.querySelector(
        "[data-main-infinite-retry]"
      );
      let nextUrl = infiniteScroll.dataset.nextUrl;
      let isLoading = false;
      let observer = null;
      let isInfiniteScrollNear = false;
      let lastInfiniteLoadScrollPosition = null;
      let infiniteScrollFrame = null;
      let isInitialNutritionScan =
        hasActiveNutritionBounds && updateProductCount() < 16 && Boolean(nextUrl);
      const pendingNutritionCards = [];
      let nutritionLoadingMessage = null;

      const appendProductCards = (cards) => {
        if (!cards.length) return;
        const fragment = document.createDocumentFragment();
        cards.forEach((card) => fragment.appendChild(card));
        productGrid.appendChild(fragment);
        productGrid.dispatchEvent(
          new CustomEvent("theme:rimg:watch", { bubbles: true })
        );
      };

      const finishInitialNutritionScan = () => {
        appendProductCards(pendingNutritionCards.splice(0));
        isInitialNutritionScan = false;
        root.classList.remove("is-nutrition-filtering");
        productGrid.removeAttribute("aria-busy");
        if (nutritionLoadingMessage?.parentNode) {
          nutritionLoadingMessage.parentNode.removeChild(nutritionLoadingMessage);
        }
        nutritionLoadingMessage = null;
        updateProductCount();
      };

      if (isInitialNutritionScan) {
        root.classList.add("is-nutrition-filtering");
        productGrid.setAttribute("aria-busy", "true");
        nutritionLoadingMessage = document.createElement("div");
        nutritionLoadingMessage.className = "main-collection__nutrition-loading";
        nutritionLoadingMessage.setAttribute("role", "status");

        const loadingLabel = document.createElement("p");
        loadingLabel.className = "main-collection__nutrition-loading-label";
        loadingLabel.textContent = "Finding matching products…";
        nutritionLoadingMessage.appendChild(loadingLabel);

        const skeletonGrid = document.createElement("div");
        skeletonGrid.className = "main-collection__nutrition-skeleton-grid";
        skeletonGrid.setAttribute("aria-hidden", "true");
        for (let skeletonIndex = 0; skeletonIndex < 4; skeletonIndex += 1) {
          const skeletonCard = document.createElement("span");
          skeletonCard.className = "main-collection__nutrition-skeleton-card";
          skeletonCard.innerHTML =
            '<span class="main-collection__nutrition-skeleton-image"></span>' +
            '<span class="main-collection__nutrition-skeleton-line is-short"></span>' +
            '<span class="main-collection__nutrition-skeleton-line"></span>' +
            '<span class="main-collection__nutrition-skeleton-line is-price"></span>';
          skeletonGrid.appendChild(skeletonCard);
        }
        nutritionLoadingMessage.appendChild(skeletonGrid);
        productGrid.parentNode?.insertBefore(nutritionLoadingMessage, productGrid);
        const noProducts = root.querySelector("[data-main-no-products]");
        if (noProducts) noProducts.hidden = true;
      }

      const setStatus = (message) => {
        if (status) status.textContent = message;
      };

      const showManualLoad = (label = "Load more products") => {
        if (!retryButton) return;
        retryButton.textContent = label;
        retryButton.hidden = false;
      };

      const completeInfiniteScroll = () => {
        nextUrl = "";
        delete infiniteScroll.dataset.nextUrl;
        infiniteScroll.classList.remove("is-loading", "is-error");
        infiniteScroll.classList.add("is-complete");
        setStatus("All products loaded");
        if (retryButton) retryButton.hidden = true;
        if (observer) observer.disconnect();
      };

      const loadMoreProducts = async () => {
        if (isLoading || !nextUrl) return;

        let shouldContinueNutritionLoading = false;
        isLoading = true;
        infiniteScroll.classList.remove("is-error", "is-complete");
        infiniteScroll.classList.add("is-loading");
        setStatus("Loading more products");
        if (retryButton) retryButton.hidden = true;

        try {
          const requestUrl = new URL(nextUrl, window.location.origin);
          const currentUrl = new URL(window.location.href);

          ["view", "preview_theme_id", ...customNutritionParams].forEach((parameterName) => {
            if (
              currentUrl.searchParams.has(parameterName) &&
              !requestUrl.searchParams.has(parameterName)
            ) {
              requestUrl.searchParams.set(
                parameterName,
                currentUrl.searchParams.get(parameterName)
              );
            }
          });

          const requestUrls = [requestUrl];
          if (isInitialNutritionScan) {
            const firstPageNumber = Number(requestUrl.searchParams.get("page"));
            const nutritionBatchSize = 4;
            if (Number.isFinite(firstPageNumber) && filteredPageCount > firstPageNumber) {
              for (let offset = 1; offset < nutritionBatchSize; offset += 1) {
                const pageNumber = firstPageNumber + offset;
                if (pageNumber > filteredPageCount) break;
                const pageUrl = new URL(requestUrl.toString());
                pageUrl.searchParams.set("page", String(pageNumber));
                requestUrls.push(pageUrl);
              }
            }
          }

          const loadedPages = await Promise.all(
            requestUrls.map(async (pageUrl) => {
              const response = await fetch(pageUrl.toString(), {
                credentials: "same-origin",
                headers: {
                  "X-Requested-With": "XMLHttpRequest",
                },
              });

              if (!response.ok) {
                throw new Error("Unable to load more products");
              }

              const responseHtml = await response.text();
              const responseDocument = new DOMParser().parseFromString(
                responseHtml,
                "text/html"
              );
              const pageRoot = responseDocument.querySelector(
                "[data-main-collection]"
              );
              const pageGrid = pageRoot?.querySelector(
                ".main-collection__variant-grid"
              );

              if (!pageRoot || !pageGrid) {
                throw new Error("Unable to find the next products");
              }

              return { pageRoot, pageGrid };
            })
          );

          const existingProductIds = new Set(
            Array.from(
              productGrid.querySelectorAll("[data-main-product-id]")
            ).map((card) => card.dataset.mainProductId)
          );
          pendingNutritionCards.forEach((card) => {
            if (card.dataset.mainProductId) {
              existingProductIds.add(card.dataset.mainProductId);
            }
          });

          const newCards = loadedPages.flatMap(({ pageGrid }) =>
            Array.from(pageGrid.children).filter((card) => {
              const productId = card.dataset.mainProductId;
              if (!productId || existingProductIds.has(productId)) return false;
              if (!cardMatchesNutritionBounds(card)) return false;

              existingProductIds.add(productId);
              return true;
            })
          );

          if (isInitialNutritionScan) {
            pendingNutritionCards.push(...newCards);
          } else {
            appendProductCards(newCards);
          }

          const lastLoadedRoot = loadedPages[loadedPages.length - 1].pageRoot;
          const nextInfiniteScroll = lastLoadedRoot.querySelector(
            "[data-main-infinite-scroll]"
          );
          nextUrl = nextInfiniteScroll?.dataset.nextUrl || "";
          const visibleProductCount =
            updateProductCount() + pendingNutritionCards.length;

          if (
            isInitialNutritionScan &&
            (visibleProductCount >= 16 || !nextUrl)
          ) {
            finishInitialNutritionScan();
          }

          shouldContinueNutritionLoading =
            isInitialNutritionScan && visibleProductCount < 16 && Boolean(nextUrl);

          if (!nextUrl) {
            completeInfiniteScroll();
          } else {
            infiniteScroll.dataset.nextUrl = nextUrl;
            infiniteScroll.classList.remove("is-loading");
            setStatus("");

            if (!observer) {
              showManualLoad();
            }
          }
        } catch (error) {
          if (isInitialNutritionScan) finishInitialNutritionScan();
          infiniteScroll.classList.remove("is-loading");
          infiniteScroll.classList.add("is-error");
          setStatus("More products could not be loaded");
          showManualLoad("Try again");
        } finally {
          isLoading = false;
          if (shouldContinueNutritionLoading) {
            window.setTimeout(loadMoreProducts, 0);
          }
        }
      };

      const maybeLoadMoreProducts = () => {
        const currentScrollPosition = window.scrollY || 0;
        const hasMovedDownEnough =
          lastInfiniteLoadScrollPosition === null ||
          currentScrollPosition >= lastInfiniteLoadScrollPosition + 160;

        if (
          !isInfiniteScrollNear ||
          !hasMovedDownEnough ||
          isLoading ||
          !nextUrl
        ) {
          return;
        }

        lastInfiniteLoadScrollPosition = currentScrollPosition;
        loadMoreProducts();
      };

      if (retryButton) {
        retryButton.addEventListener("click", loadMoreProducts);
      }

      if ("IntersectionObserver" in window) {
        observer = new IntersectionObserver(
          (entries) => {
            isInfiniteScrollNear = entries.some(
              (entry) => entry.isIntersecting
            );
            maybeLoadMoreProducts();
          },
          { rootMargin: "250px 0px" }
        );
        observer.observe(infiniteScroll);

        window.addEventListener(
          "scroll",
          () => {
            if (infiniteScrollFrame) {
              window.cancelAnimationFrame(infiniteScrollFrame);
            }

            infiniteScrollFrame = window.requestAnimationFrame(
              maybeLoadMoreProducts
            );
          },
          { passive: true }
        );
      } else {
        showManualLoad();
      }

      if (hasActiveNutritionBounds && updateProductCount() < 16 && nextUrl) {
        loadMoreProducts();
      }
    }

    root.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && filterPanel?.classList.contains("is-open")) {
        closeFilters();
      }
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll("[data-main-collection]").forEach(initMainCollection);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();
